import React, { useEffect, useState } from 'react';
import {
  Box, List, ListItem, ListItemText, Typography,
  Avatar, Button, CardMedia
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import InviteFriends from '../../components/profile/friends/InviteFriends';
import useDeviceInfo from '../../utils/useDeviceInfo';
import ZenSpinner from '../../components/ZenSpinner';
import { useSocket } from '../../contexts/SocketContext';
import { useTranslation } from 'react-i18next';

const RoomList = () => {
  const { t } = useTranslation();
  const { currentUser, refreshUser } = useAuth();
  const socket = useSocket();
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { paddingBottom } = useDeviceInfo();

  useEffect(() => {
    if (socket && currentUser) {
      socket.emit('fetchRooms', { token: localStorage.getItem('token') });

      socket.on('roomsList', (roomsData) => {
        const sortedRooms = roomsData.sort((a, b) => {
          const dateA = new Date(getLastMeditationDateRaw(a)).getTime();
          const dateB = new Date(getLastMeditationDateRaw(b)).getTime();
          return dateB - dateA;
        });

        const raoulRoom = {
          _id: 'raoul-ai',
          isRaoul: true,
          groupName: 'Raoul',
          groupAvatar: 'https://data.heyraoul.fr/img/avatars/user/004.jpg',
          userIds: []
        };

        setRooms([raoulRoom, ...sortedRooms]);
        setLoading(false);
      });

      return () => {
        socket.off('roomsList');
      };
    }
  }, [socket, currentUser]);

  const handleJoinRoom = (roomId) => {
    navigate(roomId === 'raoul-ai' ? '/solo' : `/room/${roomId}`);
  };

  const getFriendDetails = (userId) => {
    if (!currentUser?.friends?.length) {
      refreshUser?.();
      return null;
    }
    return currentUser.friends.find(friend => friend._id === userId) || null;
  };

  const getRoomDisplayName = (room) => {
    if (room.isRaoul) return 'Raoul';
    if (room.groupName) return room.groupName;
    const otherUserId = room.userIds.find(userId => userId !== currentUser.userId);
    const otherUser = getFriendDetails(otherUserId);
    return otherUser ? `${otherUser.firstName || t('unknown_name')} ${otherUser.lastName || ''}` : t('unknown_user');
  };

  const getRoomAvatar = (room) => {
    if (room.isRaoul) return 'https://data.heyraoul.fr/img/avatars/user/004.jpg';
    if (room.groupAvatar) return room.groupAvatar;
    const otherUserId = room.userIds.find(userId => userId !== currentUser.userId);
    const otherUser = getFriendDetails(otherUserId);
    return otherUser?.avatar || '/default-avatar.png';
  };

  const formatLastMeditationDate = (date) => {
    const today = new Date();
    const meditationDate = new Date(date);
    today.setHours(0, 0, 0, 0);
    meditationDate.setHours(0, 0, 0, 0);
    const diffDays = Math.floor((today - meditationDate) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return t('online_today');
    if (diffDays === 1) return t('online_yesterday');
    return t('online_days', { days: diffDays });
  };

  const getLastMeditationDateRaw = (room) => {
    if (room.userIds.length === 2) {
      const otherUserId = room.userIds.find(userId => userId !== currentUser.userId);
      const otherUser = getFriendDetails(otherUserId);
      return otherUser?.lastMeditationDate || null;
    } else {
      let latest = null;
      room.userIds.forEach(userId => {
        const user = getFriendDetails(userId);
        const userDate = user?.lastMeditationDate ? new Date(user.lastMeditationDate) : null;
        if (userDate && (!latest || userDate > latest)) latest = userDate;
      });
      return latest || null;
    }
  };

  const getLastMeditationDate = (room) => {
    if (room.isRaoul) return t('online_now'); // statique pour Raoul
    const rawDate = getLastMeditationDateRaw(room);
    return rawDate ? formatLastMeditationDate(rawDate) : t('no_recent_meditation');
  };

  const handleCTAClick = () => {
    navigate('/today', { state: { openFriendsDrawer: true } });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '84vh' }}>
        <ZenSpinner />
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 3 }}>
      <InviteFriends />
      <Box sx={{ px: 0, pt: 2, pb: paddingBottom }}>
        <Typography variant="h6" sx={{ mb: 1 }} gutterBottom>
          {t('meditate_with_friends')}
        </Typography>
        <List sx={{ p: 0 }}>
          {rooms.map((room, index) => (
            <ListItem key={index} onClick={() => handleJoinRoom(room._id)} sx={{ p: 0, mt: 0.5 }}>
              <Avatar
                alt={getRoomDisplayName(room)}
                src={getRoomAvatar(room)}
                sx={{ mr: 2, border: '1px solid #ddd', width: 52, height: 52 }}
              />
              <ListItemText
                primary={getRoomDisplayName(room)}
                secondary={getLastMeditationDate(room)}
                sx={{
                  '& .MuiTypography-root': { fontWeight: 'bold' },
                  '& .MuiListItemText-secondary': {
                    fontWeight: 'normal',
                    fontSize: '0.8em',
                  },
                }}
              />
            </ListItem>
          ))}
        </List>
      </Box>
    </Box>
  );
};

export default RoomList;
